import { useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import { getIntegrationInstallationKey } from '@gitbook/app-database';
import {
    Alert,
    CardBody,
    CardCheck,
    CardImage,
    CardImageLayout,
    CardRadio,
    CardSkeleton,
    Description,
    Heading,
    IconTile,
    RadioGroup,
    Step,
    StepControls,
    StepControlsSubmitButton,
    Tooltip,
} from '@gitbook/spine-react';
import { ErrorBoundary } from '@gitbook/util-debug';
import { useLoadingState } from '@gitbook/util-react-hooks';
import { assertIsDefined } from '@gitbook/util-ts';

import {
    IntegrationIcon,
    IntegrationStepWrapper,
    useInstallIntegration,
} from '../../../../Components';
import { apiQuery } from '../../../../state';
import type { GitSyncInstallationStepProps } from '../types';

/**
 * Git provider configuration step inside a GitSync installation flow.
 */
export function InstallationStep(props: GitSyncInstallationStepProps) {
    const headingId = React.useId();

    return (
        <IntegrationStepWrapper
            title="Select your Git provider"
            headingId={headingId}
            description="Choose the git provider where your content is hosted."
        >
            <ErrorBoundary
                fallback={
                    <Alert
                        kind="danger"
                        title="Error while fetching Git Sync integration"
                        details="Something went wrong while loading the Git Sync integrations available. Please try reloading the app."
                    />
                }
            >
                <React.Suspense
                    fallback={
                        <div className="flex flex-col gap-4">
                            <CardSkeleton />
                            <CardSkeleton />
                        </div>
                    }
                >
                    <GitProviderPicker {...props} headingId={headingId} />
                </React.Suspense>
            </ErrorBoundary>
        </IntegrationStepWrapper>
    );
}

/**
 * Fetch and display cards of GitSync integrations.
 */
function GitProviderPicker(props: GitSyncInstallationStepProps & { headingId: string }) {
    const [
        {
            data: { items: gitSyncIntegrations },
        },
        { data: installedIntegrations },
    ] = useSuspenseQueries({
        queries: [
            apiQuery('listIntegrations', { category: api.IntegrationCategory.Gitsync }),
            apiQuery('listOrganizationIntegrationsStatus', {
                organizationId: props.organization.id,
                target: 'organization',
            }),
        ],
    });

    assertIsDefined(installedIntegrations, 'Expected installed integrations to be defined');

    const [pickedIntegration, setPickedIntegration] = React.useState<api.Integration | null>(null);
    const installatonState = useLoadingState();

    const gitSyncIntegrationsInstalledOnOrg = React.useMemo(() => {
        return gitSyncIntegrations.filter((integration) => {
            return installedIntegrations.items.find(
                (installed) => installed.integration.name === integration.name
            );
        });
    }, [gitSyncIntegrations, installedIntegrations]);

    return (
        <Step>
            {gitSyncIntegrationsInstalledOnOrg.length === 0 &&
            !props.organization.permissions.installIntegration ? (
                <div className="mb-3">
                    <Alert
                        kind="info"
                        size="small"
                        title={`You don't have permissions to install new Git providers.`}
                        details="To get started, please ask an administrator to install its integration for your organization."
                    />
                </div>
            ) : null}

            <RadioGroup
                onChange={(value) => {
                    const integration = gitSyncIntegrations.find(({ name }) => name === value);
                    assertIsDefined(integration, `Picked integration ${value} not found`);

                    setPickedIntegration(integration);
                }}
                isDisabled={installatonState[0]}
                aria-labelledby={props.headingId}
            >
                {gitSyncIntegrations.map((integration) => (
                    <GitSyncProviderCard
                        {...props}
                        key={integration.name}
                        integration={integration}
                        isInstalledOnOrg={gitSyncIntegrationsInstalledOnOrg.some(
                            (installed) => installed.name === integration.name
                        )}
                    />
                ))}
            </RadioGroup>
            <StepControls>
                <div className="flex w-full justify-end">
                    {pickedIntegration ? (
                        <GitProvidersSubmitButton
                            {...props}
                            integration={pickedIntegration}
                            isInstalledOnOrg={gitSyncIntegrationsInstalledOnOrg.some(
                                (installed) => installed.name === pickedIntegration.name
                            )}
                        />
                    ) : (
                        <StepControlsSubmitButton disabled />
                    )}
                </div>
            </StepControls>
        </Step>
    );
}

/**
 * Submit button for the GitSync provider selection step.
 *
 * This button will install the selected integration in the organization (if needed) and space.
 */
function GitProvidersSubmitButton(props: {
    integration: api.Integration;
    isInstalledOnOrg: boolean;
    space: api.Space;
    onNext: (integration: api.Integration) => void;
}) {
    const { integration, isInstalledOnOrg, space, onNext } = props;
    const { installIntegrationInSpace } = useIntegrationInstallAction({
        integration,
        isInstalledOnOrg,
        organizationId: space.organization,
    });

    return (
        <StepControlsSubmitButton
            onGoNext={async () => {
                await installIntegrationInSpace({ space: space.id, suppressToast: true });

                onNext(integration);
            }}
        />
    );
}
/**
 * Card displaying a GitSync integration details.
 */
function GitSyncProviderCard(
    props: GitSyncInstallationStepProps & {
        integration: api.Integration;
        isInstalledOnOrg: boolean;
    }
) {
    const { integration, space, isInstalledOnOrg, onNext } = props;

    const { isInstalledInSpace, canInstallInOrg, canInstallInSpace } = useIntegrationStatus({
        integrationName: integration.name,
        organization: props.organization,
        space: space,
    });

    React.useEffect(() => {
        // Skip step if already installed in space, edge case, where the integration is installed but not configured.
        if (isInstalledInSpace) {
            onNext(integration);
        }
    }, [integration, isInstalledInSpace, onNext]);

    const needsOrgAdmin = !isInstalledOnOrg && !canInstallInOrg;

    const CardElement = (
        <CardRadio
            value={integration.name}
            isDisabled={needsOrgAdmin || !canInstallInSpace || isInstalledInSpace}
            className={'flex items-center gap-4 rounded-md pr-4'}
        >
            <CardImageLayout>
                <CardImage>
                    <IconTile size={48} className="bg-white">
                        <IntegrationIcon integration={integration} className="size-full" />
                    </IconTile>
                </CardImage>
                <CardBody>
                    <Heading>{integration.title}</Heading>
                    <Description>{integration.description}</Description>
                </CardBody>
            </CardImageLayout>
            <CardCheck className="ml-auto" />
        </CardRadio>
    );

    return needsOrgAdmin ? (
        <Tooltip
            label={`You don't have permissions to install ${integration.title}. Please ask an administrator to install it for your organization.`}
        >
            <div>{CardElement}</div>
        </Tooltip>
    ) : (
        CardElement
    );
}

/**
 * A utility hook to get the status of an integration in an organization and space.
 */
function useIntegrationStatus(props: {
    integrationName: string;
    organization: api.Organization;
    space: api.Space;
}) {
    const { integrationName, organization, space } = props;

    const installationId = getIntegrationInstallationKey(integrationName, {
        organization: organization.id,
    });

    const [{ data: spaceInstallation }] = useSuspenseQueries({
        queries: [
            apiQuery(
                'getIntegrationSpaceInstallation',
                {
                    integrationName,
                    installationId,
                    spaceId: space.id,
                    extended: true,
                },
                { nullOnError: true }
            ),
        ],
    });

    return {
        canInstallInOrg: organization.permissions.installIntegration,
        canInstallInSpace: space.permissions.installIntegration,
        isInstalledInSpace: Boolean(spaceInstallation),
    };
}

/**
 * A utility hook to install an integration in a space.
 */
function useIntegrationInstallAction(props: {
    integration: api.Integration;
    organizationId: string;
    isInstalledOnOrg: boolean;
}) {
    const { integration, organizationId, isInstalledOnOrg } = props;

    const [installIntegrationInSpace] = useInstallIntegration(
        integration,
        { organization: organizationId },
        isInstalledOnOrg
    );

    return {
        installIntegrationInSpace,
    };
}
